import { TAU, clamp, damp } from '../core/math';
import { rr } from '../core/rng';
import { BOSS_BY_ID } from '../data/bosses';
import { BOSS_PHASE_EXTRA } from '../data/codex';
import { SFX } from '../platform/audio';
import { warnBurst, warnCircle, warnLine } from '../render/warn-fx';
import { P } from '../world/player';
import { hurtPlayer } from './player';
import { STATE, hitstop, schedule, shake, spawnDebris, spawnRing } from './fx';
import { camPunch } from './camera';
import { banner } from '../ui/hud';
import { rollType, spawnEnemy } from './spawn';
import { coverNearest, destroyObstacle, groundYAt } from '../world/arena';
import { enemies } from '../entities/enemies';
/* ============================================================
   五隻 BOSS 的行為 —— 招式、階段、召喚
   ============================================================

   `.claude/notes/boss-map.md` 盤過：**原型完全沒有戰鬥設計**，只有造型與
   動畫。所以招式是我們自己依規格 §5.2 的 P1→P2 描述設計的，
   規則是「造型已經在演什麼，招式就得是什麼」——
   KATHEDRALE 的三牆會外翻，那牠的招式就必須跟掩體有關。

   ## 骨架與小怪同一套

     0 巡航 ─→ 1 預備(tTel) ─→ 2 攻擊(tAtk) ─→ 3 恢復(tRec) ─┐
     ↑                                                        │
     └────────────────────────────────────────────────────────┘

   `e.tel`/`e.atk` 是 0→1 的進度，**直接餵給造型的 `upd(rig, o, dt)`** ——
   「牠正在蓄力」是造型自己講的，行為層不畫任何東西。地面預警補的是
   另一半：造型講「牠要出手了」，地面圖形講「你該往哪閃」。

   ## 階段推進有一套共用的轉場層

   規格 §8 已知限制第 7 條自己要求的。血量跨過門檻 → 無敵 1.2 秒 + 頓幀 +
   鏡頭震 + 從變形部位噴碎片 + 橫幅講這一階段在做什麼。一套跑完所有 Boss，
   而**造型的變形是各自的 `upd()` 用 `s1`/`s2` 演的**，這一層不碰造型。

   ## 四色語彙在 Boss 身上照樣成立（規格 §2.3）

   紅＝接觸（只有 WALZE 有 `T.dmg`）、橙＝直射、紫＝落點、天藍＝生成。
   MUTTER 與 KATHEDRALE 的二階段文案寫著「天藍→紫」「橙→紫」，
   所以牠們進二階段之後**真的**多一路落點攻擊，而不是把數字調大。 */

/** 排程回呼不能捉住敵人物件（牠可能已經回池了）。`eShoot` 只讀這四個欄位。 */
function stub(x, z, r, y) { return { pos: { x: x, z: z }, r: r, T: { y: y } }; }

/** 落點爆炸。**只打玩家不打敵人** —— 它是衝著玩家來的，不該幫玩家清場。 */
function blast(x, z, radius, dmg, hex) {
  warnBurst(x, z, radius, hex);
  spawnRing(x, z, hex, 0.6, radius * 1.6, 0.35);
  /* Boss 的落點爆炸打得比雜兵高得多（半徑 ×1.6）——
     **飛起來躲不掉 Boss**，那是刻意的：飛行是雜兵海的解，不是 Boss 戰的解。 */
  if (P.alive && Math.hypot(P.pos.x - x, P.pos.z - z) < radius) hurtPlayer(dmg, groundYAt(x, z) + radius * 1.6);
}

/** 預警 → 落點。`t` 是預警時間，規格 §6.3 的下限 0.26 秒對 Boss 一樣適用。 */
function drop(x, z, radius, dmg, hex, t) {
  warnCircle(x, z, radius, t, hex);
  schedule(t, function () { blast(x, z, radius, dmg, hex); });
}

/** 召喚。**只生已登場過的敵種**（`rollType` 吃當下時間的權重表）——
    Boss 戰不是初次見面的場合（設計文件 §3 第 2 點）。 */
function summonWave(e, n, dist) {
  var got = 0;
  for (var i = 0; i < n; i++) {
    var a = rr(0, TAU);
    var s = spawnEnemy(rollType(STATE.t), e.pos.x + Math.cos(a) * dist, e.pos.z + Math.sin(a) * dist);
    /* 認養：護盾要「清完才破」，就得數得出來哪幾隻是牠召的。
       `spawnEnemy` 每次生成都會把這個欄位清成 null（池化物件不清會認錯人）。 */
    if (s) { s.bornBy = e; got++; }
  }
  spawnRing(e.pos.x, e.pos.z, e.T.accent, 0.8, dist * 2.4, 0.5);
  SFX.alarm();
  return got;
}

/* ---------- 階段推進（共用轉場層） ---------- */

function phaseText(e, n) {
  var B = BOSS_BY_ID[e.type];
  if (B.pn && B.pn[n]) return B.pn[n];
  if (n === 1 && B.p2) return B.p2;
  var x = BOSS_PHASE_EXTRA[e.type];
  return x && x[n - 1] ? x[n - 1] : '型態改變';
}

/** 轉場：一套跑完所有 Boss。**無敵 1.2 秒**是給玩家看變形用的 ——
    變形演到一半就被打死的話，那一段造型等於沒做。 */
function enterPhase(e, n) {
  e.stg = n;
  e.invulnT = 1.2;
  e.st = 0; e.stT = 0; e.cd = 1.0;
  hitstop(0.14); shake(1.1); camPunch(1.0); SFX.alarm();
  spawnRing(e.pos.x, e.pos.z, e.T.accent2, 1, e.r * 6, 0.75);
  spawnRing(e.pos.x, e.pos.z, 0xffffff, 1, e.r * 3.4, 0.45);
  /* 從變形的部位噴碎片。高度取機體中段 —— 從腳下噴會讀成「地面爆炸」。 */
  spawnDebris(e.pos.x, e.T.worldH * 0.45, e.pos.z, e.T.body, 26, 1.8, e.r * 0.3);
  banner('型態 ' + (n + 1) + ' · ' + phaseText(e, n));
  /* 生成型與最終 Boss 在轉場時召一波 —— 設計文件 §3：
     「Boss 進無敵、召一波、清完才破盾」。其餘三隻不召（那是牠們的機制，不是通則）。 */
  if (e.type === 'mutter' || e.type === 'kaiser') {
    e.adds = summonWave(e, e.type === 'kaiser' ? 8 : 5, 11);
    /* **一隻都沒召出來就不要開護盾**（池子滿了、同屏上限）——
       開了會立刻在下一步破掉，玩家看到的是一閃而過的「護盾破除」，
       讀起來像 bug 而不是機制。 */
    /* 護盾的逾時：KAISER 短一點。牠有兩次轉場，而牠只有 90 秒的窗口
       （18:30 降臨、20:00 撤離）—— 兩次各站著等 9 秒等於少掉五分之一的戰鬥時間。 */
    if (e.adds > 0) { e.shield = 1; e.shieldT = e.type === 'kaiser' ? 6 : 9; }
  }
}

/** 血量跨過門檻就推進。門檻表在 `data/boss-stats.ts`（兩階段 0.5、三階段 0.66/0.33）。 */
function checkPhase(e) {
  var ph = e.T.phases, half = e.hp / e.hpMax;
  while (e.stg < ph.length && half < ph[e.stg]) enterPhase(e, e.stg + 1);
}

/** 召喚護盾：**清完才破盾**。逾時也破 —— 召出來的那批可能被別的東西
    收掉（大招、殉爆），沒有逾時的話玩家會卡在一個永遠打不動的 Boss 前面。 */
function updateShield(e, dt) {
  if (!e.shield) return;
  e.shieldT -= dt;
  var left = 0;
  for (var i = 0; i < enemies.length; i++) if (enemies[i].bornBy === e) left++;
  e.adds = left;
  if (left <= 0 || e.shieldT <= 0) {
    e.shield = 0;
    spawnRing(e.pos.x, e.pos.z, 0xffffff, e.r, e.r * 7, 0.5);
    hitstop(0.1); shake(0.6); SFX.boom();
    banner('護盾破除 · 現在打得動了');
  }
}

/* ---------- 招式 ---------- */

/** 橙・直射：一條瞄準線 + 連發。會被地形擋（規格 §2.3），所以起手要看得到。 */
function bolt(e, nx, nz, sh, n, gap, spread, speed, dmg) {
  for (var i = 0; i < n; i++) {
    var d = i * gap;
    schedule(d, (function (x, z, r, y, sp) {
      return function () { sh(stub(x, z, r, y), nx, nz, sp, speed, dmg); };
    })(e.pos.x, e.pos.z, e.r, e.T.y, (i - (n - 1) / 2) * spread));
  }
}

/** 紫・環形彈幕。 */
function radial(e, sh, n, speed, dmg, jitter) {
  for (var i = 0; i < n; i++) {
    var a = (i / n) * TAU + (jitter ? rr(-0.06, 0.06) : 0);
    sh(e, Math.sin(a), Math.cos(a), 0, speed, dmg);
  }
  spawnRing(e.pos.x, e.pos.z, e.T.accent, 1, e.r * 3.2, 0.4);
}

function fireBoss(e, nx, nz, sh, shock) {
  var T = e.T, dm = e.dmgMul, p2 = e.stg >= 1;
  var px = P.pos.x, pz = P.pos.z;
  if (e.type === 'walze') {
    /* 橙的砲塔：三連直射。核心被打掉就只剩一半威力（這是弱點的實質獎勵）。 */
    bolt(e, nx, nz, sh, p2 ? 4 : 3, 0.12, 0.10, 34, (e.broken ? 9 : 18) * dm);
    warnLine(e.pos.x, e.pos.z, nx, nz, T.rng, 0.3, T.accent2);
    if (p2) radial(e, sh, 10, 20, 12 * dm, true);       // 二階段砲塔自轉＝多一路
    shake(0.18);
  } else if (e.type === 'zenit') {
    /* 紫・落點：砲莢輪流丟。每破一具就少一組（`podDead`）。 */
    var pods = Math.max(1, (p2 ? 4 : 3) - (e.podDead || 0));
    for (var i = 0; i < pods; i++) {
      var a = rr(0, TAU), d = rr(0, 7);
      drop(px + Math.cos(a) * d, pz + Math.sin(a) * d, 5.2, 26 * dm, T.accent, 0.85);
    }
    if (p2) radial(e, sh, 14, 18, 12 * dm, true);
  } else if (e.type === 'mutter') {
    /* 天藍・生成是本體機制：每次出手就是一批。二階段核心直射（天藍→紫）。 */
    e.adds = summonWave(e, p2 ? 5 : 4, 9);
    if (p2) {
      bolt(e, nx, nz, sh, 5, 0.1, 0.14, 26, 15 * dm);
      warnLine(e.pos.x, e.pos.z, nx, nz, T.rng, 0.3, T.accent2);
    }
  } else if (e.type === 'kathedrale') {
    /* 橙・直射，而且**把掩體轟掉** —— 這一場教的就是「你躲的掩體會消失」。
       目標挑玩家身邊最近的那一塊：轟的是他正在用的那個掩體，不是隨機一塊。 */
    var ob = coverNearest(px, pz, 16);
    if (ob) {
      warnCircle(ob.x, ob.z, ob.br + 3.4, 1.1, T.accent);
      schedule(1.1, (function (o) {
        return function () {
          destroyObstacle(o);
          blast(o.x, o.z, o.br + 4.2, 30 * dm, 0xff8a3d);
          spawnDebris(o.x, 1.2, o.z, 0x6b3a18, 22, 1.6, 0.6);
          shake(0.9);
        };
      })(ob));
    }
    bolt(e, nx, nz, sh, p2 ? 6 : 4, 0.11, 0.13, 30, 20 * dm);
    warnLine(e.pos.x, e.pos.z, nx, nz, T.rng, 0.36, T.accent2);
    if (p2) radial(e, sh, 16, 17, 13 * dm, true);
    shake(0.3);
  } else {
    /* KAISER：三階段各一種打法。 */
    if (e.stg === 0) {
      bolt(e, nx, nz, sh, 5, 0.09, 0.12, 36, 24 * dm);
      warnLine(e.pos.x, e.pos.z, nx, nz, T.rng, 0.36, T.accent);
      shake(0.35);
    } else if (e.stg === 1) {
      /* 固定砲台：高頻齊射 + 三發落點轟炸。 */
      bolt(e, nx, nz, sh, 8, 0.07, 0.10, 40, 22 * dm);
      for (var k = 0; k < 3; k++) {
        var aa = rr(0, TAU), dd = rr(2, 9);
        drop(px + Math.cos(aa) * dd, pz + Math.sin(aa) * dd, 6.0, 30 * dm, T.accent2, 0.9 + k * 0.18);
      }
      shock(e.pos.x, e.pos.z, 22 * dm, 34, 22);
      shake(0.7);
    } else {
      /* 上半身脫離之後：螺旋彈幕（浮空，打法完全不同）。 */
      e.spiralA = (e.spiralA || 0) + 0.42;
      for (var s = 0; s < 3; s++) {
        var sa = e.spiralA + s * (TAU / 3);
        sh(e, Math.sin(sa), Math.cos(sa), 0, 22, 18 * dm);
      }
      radial(e, sh, 18, 16, 16 * dm, true);
      shake(0.4);
    }
  }
}

/* ---------- 主體 ---------- */

/** 想維持的交戰距離（**世界單位**，不是射程的比例）。

    ⚠️ 這一組數字的上限是**鏡頭看得到的範圍**，不是射程。
    戰場鏡頭在 Boss 拉遠之後，玩家周圍大約 ±20 單位在畫面內；
    而 KATHEDRALE 的射程是 23.4、KAISER 是 31.2 —— 照射程訂交戰帶的話，
    牠們會站在畫面外對你開火，玩家只看得到血條與飛過來的彈幕。
    實看截圖抓到的就是這件事（第一版用「射程 × 比例」，兩隻都不在畫面裡）。

    WALZE 是 0：牠是紅的，威脅走接觸，要貼上來。 */
var HOLD = { walze: 0, zenit: 9, mutter: 11, kathedrale: 11, kaiser: 12 };

function seek(e, dt, nx, nz, want) {
  var sp = e.T.spd * (e.legDead ? 0.5 : 1) * want;
  e.vel.x = damp(e.vel.x, nx * sp, 4, dt);
  e.vel.z = damp(e.vel.z, nz * sp, 4, dt);
}

/** 每一步跑一次。位移、地形、接觸傷害仍由 `sim/enemies.ts` 統一處理。 */
export function bossAI(e, dt, nx, nz, dist, sh, shock) {
  var T = e.T;
  if (e.invulnT > 0) e.invulnT -= dt;
  checkPhase(e);
  updateShield(e, dt);
  e.cd -= dt;

  var v = Math.hypot(e.vel.x, e.vel.z);
  e.mv = Math.min(1, v / Math.max(0.5, T.spd));
  if (e.st === 0) e.ph = (e.ph + dt * (T.gait || 1) * (0.34 + 0.66 * e.mv)) % 1;

  /* KAISER 的二階段是**單膝跪地的固定砲台** —— 造型已經跪下去了，
     這裡就不能再讓牠走路，否則會看到一台跪著滑行的機甲。 */
  var rooted = e.type === 'kaiser' && e.stg === 1;
  var band = HOLD[e.type] === undefined ? 11 : HOLD[e.type];
  /* **離得太遠就用力追。** 玩家的移速是 11.5，最快的 Boss 是 7 ——
     沒有這一段的話，只要玩家往外跑，Boss 就永遠回不到畫面裡。
     ×1.9 讓牠追得回來，但進到交戰帶就恢復原速（否則牠會直接撞上來，
     慢速砲台型的手感整個變掉）。 */
  var far = dist > band + 14;
  var want = rooted ? 0 : (dist > band + 3 ? (far ? 1.9 : 1) : (dist < band - 3 ? -0.5 : 0));

  if (e.st === 0) {
    e.tel = damp(e.tel, 0, 9, dt); e.atk = damp(e.atk, 0, 11, dt);
    seek(e, dt, nx, nz, want);
    if (dist < T.rng && e.cd <= 0 && !e.shield) { e.st = 1; e.stT = T.tTel; }
  } else if (e.st === 1) {
    e.stT -= dt;
    e.tel = 1 - Math.max(0, e.stT) / T.tTel;
    seek(e, dt, nx, nz, 0);                       // 蓄力站定：預備動作要讀得出來
    if (e.stT <= 0) { e.st = 2; e.stT = T.tAtk; e.tel = 1; fireBoss(e, nx, nz, sh, shock); }
  } else if (e.st === 2) {
    e.stT -= dt;
    e.atk = 1 - Math.max(0, e.stT) / T.tAtk;
    /* WALZE 的二階段是「車體下沉＝衝撞判定變大」，所以出手那一段真的往前衝。 */
    seek(e, dt, nx, nz, e.type === 'walze' && e.stg >= 1 ? 2.4 : 0);
    if (e.stT <= 0) { e.st = 3; e.stT = T.tRec; e.atk = 1; }
  } else {
    e.stT -= dt;
    e.tel = damp(e.tel, 0, 8, dt);
    e.atk = damp(e.atk, 0, 10, dt);
    seek(e, dt, nx, nz, want * 0.5);
    if (e.stT <= 0) {
      e.st = 0;
      /* 階段越後面出手越密。冷卻**不隨機**：Boss 一場只有一隻，
         隨機化的節奏在這裡只會讓玩家讀不出規律。 */
      e.cd = clamp(1.5 - e.stg * 0.35, 0.5, 1.6);
    }
  }
  /* 浮空的 Boss（ZENIT / MUTTER，以及 KAISER 的第三階段）不吃地形，
     由 `sim/enemies.ts` 的地形分支判斷；這裡只負責把旗標算出來。 */
  e.airborne = T.fly ? 1 : (e.type === 'kaiser' && e.stg >= 2 ? 1 : 0);
}
